// The tab strip of a hub (Session SC7A, the calm menu). The office menu holds 14 hubs and every other
// page is a tab of one of them, so this strip is how a person moves between the pages of a hub
// without going back to the menu.
//
// A tab is a LINK to an existing route (the page itself is reused unchanged), so a tab's address works
// when opened directly, bookmarked or refreshed, and the strip simply shows which tab the address is.
// It is drawn by OfficeLayout above the page whenever the address is a tab's own page; a record page
// (a customer, a quote) is never crowded with it.
//
// Accessibility: a tablist of links, role=tab with aria-selected, a roving tabindex (the active tab is
// the one Tab key stop), arrow keys / Home / End move between tabs and open the one they land on, and
// every tab is at least 44px tall on a phone. With many tabs the strip scrolls sideways on a phone
// (marked data-hscroll for the layout gate) and wraps on a desktop.
import { useRef, type KeyboardEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { NavTab } from "../lib/nav";
import { navPathOf } from "../lib/nav";
import { ignoreNavKeysWhenLocked, lockedLinkProps, useIsNavigationLocked } from "../lib/navigationLock";

export interface HubTabsProps {
  /** The hub's name, for the screen reader ("Customers pages"). */
  label: string;
  tabs: readonly NavTab[];
  /** Index of the tab the current address belongs to. */
  activeIndex: number;
  /** A count shown beside a tab (keyed by the tab's path), e.g. active projects. Zero or absent shows nothing. */
  badges?: Record<string, number>;
}

export function HubTabs({ label, tabs, activeIndex, badges = {} }: HubTabsProps) {
  const navigate = useNavigate();
  const refs = useRef<Array<HTMLAnchorElement | null>>([]);
  // A page in the middle of something leaving would spoil (the Release and send run) holds the navigation lock: the strip is inert.
  const locked = useIsNavigationLocked();

  function go(index: number) {
    const target = tabs[index];
    if (!target || locked) return;
    refs.current[index]?.focus();
    navigate(target.to);
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (ignoreNavKeysWhenLocked(locked, e)) return;
    let next: number | null = null;
    if (e.key === "ArrowRight") next = (activeIndex + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (activeIndex - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next === null) return;
    e.preventDefault();
    go(next);
  }

  return (
    <div role="tablist" aria-label={`${label} pages`} data-hub-tabs="" data-hscroll="" aria-disabled={locked || undefined} className={`ele-tabs mb-5 ${locked ? "opacity-60" : ""}`} onKeyDown={onKeyDown}>
      {tabs.map((tab, i) => {
        const active = i === activeIndex;
        const badge = badges[navPathOf(tab.to)] ?? 0;
        return (
          <Link
            key={tab.to}
            ref={(el) => {
              refs.current[i] = el;
            }}
            to={tab.to}
            role="tab"
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            title={tab.hint}
            {...lockedLinkProps(locked)}
            className={`ele-tab inline-flex items-center gap-1.5 whitespace-nowrap no-underline max-sm:min-h-11 ${active ? "is-active" : ""}`}
          >
            {tab.label}
            {badge > 0 ? (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-ele-green px-1 text-[9px] font-bold text-ele-bg" title={`${badge} active`}>
                {badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}
